import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, MailCheck, ShieldCheck } from "lucide-react";
import { api } from "../api";
import { useMember } from "../MemberContext";
import { AuthLayout, inputCls, btnCls } from "./Login";
import { ChipSelect, PageLoader } from "../ui";

const YEAR = new Date().getFullYear();
const DAY_LABEL = { mo: "Mo", di: "Di", mi: "Mi", do: "Do", fr: "Fr", sa: "Sa", so: "So" };
const STEPS = ["Konto", "Spielerprofil", "Ziele", "Trainingszeit", "Equipment"];
const EMPTY = { first_name: "", last_name: "", email: "", password: "", birth_year: "", parent_email: "", position: "", level: "",
  club: "", goals: [], training_days: [], session_minutes: 25, equipment: ["ball"], privacy_accepted: false };

const Field = ({ label, children }) => <div><Label className="text-[#A1A1AA]">{label}</Label><div className="mt-2">{children}</div></div>;

function StepAccount({ f, set, minor }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Vorname"><Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} className={inputCls} data-testid="reg-first-name" /></Field>
        <Field label="Nachname"><Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} className={inputCls} data-testid="reg-last-name" /></Field>
      </div>
      <Field label="E-Mail"><Input type="email" autoComplete="email" value={f.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="reg-email" /></Field>
      <Field label="Passwort (mind. 8 Zeichen)"><Input type="password" autoComplete="new-password" value={f.password} onChange={(e) => set("password", e.target.value)} className={inputCls} data-testid="reg-password" /></Field>
      <Field label="Geburtsjahr">
        <select value={f.birth_year} onChange={(e) => set("birth_year", e.target.value)} className={`${inputCls} w-full px-3 border bg-[#0A0A0A]`} data-testid="reg-birth-year">
          <option value="">Bitte wählen</option>
          {Array.from({ length: 70 }, (_, i) => YEAR - 5 - i).map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </Field>
      {minor && (
        <div className="border border-[#c1ff72]/50 bg-[#c1ff72]/[0.05] p-4 space-y-3" data-testid="reg-parent-box">
          <p className="flex gap-2 text-sm text-white"><ShieldCheck className="w-5 h-5 text-[#c1ff72] shrink-0" />Du bist jünger als 16. Deine Eltern müssen dein Konto per E-Mail bestätigen.</p>
          <Field label="E-Mail eines Elternteils"><Input type="email" value={f.parent_email} onChange={(e) => set("parent_email", e.target.value)} className={inputCls} data-testid="reg-parent-email" /></Field>
        </div>
      )}
    </div>
  );
}

function StepProfile({ f, set, meta }) {
  return (
    <div className="space-y-6">
      <Field label="Spielposition"><ChipSelect options={meta.positions} value={f.position} onChange={(v) => set("position", v)} testPrefix="reg-position" /></Field>
      <Field label="Wie möchtest du trainieren?"><ChipSelect options={meta.levels} value={f.level} onChange={(v) => set("level", v)} testPrefix="reg-level" /></Field>
      <Field label="Aktueller Verein (optional)"><Input value={f.club} onChange={(e) => set("club", e.target.value)} className={inputCls} data-testid="reg-club" /></Field>
    </div>
  );
}

const StepGoals = ({ f, set, meta }) => (
  <Field label="Wähle bis zu 3 Ziele"><ChipSelect multi max={3} options={meta.goals} value={f.goals} onChange={(v) => set("goals", v)} testPrefix="reg-goal" /></Field>
);

const StepTime = ({ f, set, meta }) => (
  <div className="space-y-6">
    <Field label="An welchen Tagen kannst du trainieren?"><ChipSelect multi options={meta.weekdays.map((d) => ({ id: d, label: DAY_LABEL[d] }))} value={f.training_days} onChange={(v) => set("training_days", v)} testPrefix="reg-day" /></Field>
    <Field label="Zeit pro Einheit"><ChipSelect options={meta.durations.map((d) => ({ id: d, label: `${d} Min` }))} value={f.session_minutes} onChange={(v) => set("session_minutes", v)} testPrefix="reg-duration" /></Field>
  </div>
);

const StepEquipment = ({ f, set, meta }) => (
  <div className="space-y-6">
    <Field label="Was hast du zur Verfügung?"><ChipSelect multi options={meta.equipment.filter((e) => e.id !== "keins")} value={f.equipment} onChange={(v) => set("equipment", v)} testPrefix="reg-equipment" /></Field>
    <label className="flex gap-3 text-sm text-[#A1A1AA] cursor-pointer">
      <Checkbox checked={f.privacy_accepted} onCheckedChange={(v) => set("privacy_accepted", !!v)} className="mt-0.5 border-white/30 data-[state=checked]:bg-[#c1ff72] data-[state=checked]:text-black" data-testid="reg-privacy" />
      <span>Ich habe die <a href="/datenschutz" target="_blank" rel="noreferrer" className="text-[#c1ff72] underline">Datenschutzhinweise</a> gelesen und bin einverstanden, dass meine Angaben zur Erstellung meiner Trainingspläne gespeichert werden.</span>
    </label>
  </div>
);

function validate(step, f, minor) {
  if (step === 0) {
    if (!f.first_name.trim() || !f.last_name.trim()) return "Bitte Vor- und Nachnamen angeben";
    if (!/^\S+@\S+\.\S+$/.test(f.email)) return "Bitte eine gültige E-Mail angeben";
    if (f.password.length < 8) return "Das Passwort braucht mindestens 8 Zeichen";
    if (!f.birth_year) return "Bitte Geburtsjahr wählen";
    if (minor && !/^\S+@\S+\.\S+$/.test(f.parent_email)) return "Bitte die E-Mail eines Elternteils angeben";
  }
  if (step === 1 && (!f.position || !f.level)) return "Bitte Position und Niveau wählen";
  if (step === 2 && !f.goals.length) return "Bitte mindestens ein Ziel wählen";
  if (step === 3 && !f.training_days.length) return "Bitte mindestens einen Trainingstag wählen";
  if (step === 4 && !f.privacy_accepted) return "Bitte die Datenschutzhinweise bestätigen";
  return "";
}

export default function Register() {
  const { meta, setMember } = useMember();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const minor = f.birth_year && YEAR - Number(f.birth_year) < (meta?.parent_consent_age || 16);
  if (!meta) return <PageLoader />;
  if (pending) return (
    <AuthLayout overline="Fast geschafft" title="Warte auf deine Eltern">
      <div className="space-y-6" data-testid="reg-pending-parent"><MailCheck className="w-12 h-12 text-[#c1ff72]" /><p className="text-[#A1A1AA]">{pending}</p>
        <Link to="/training" className="text-[#c1ff72] underline">Zur Startseite</Link></div>
    </AuthLayout>
  );
  const next = async () => {
    const e = validate(step, f, minor);
    if (e) return toast.error(e, { id: "reg-validation" });
    toast.dismiss("reg-validation");
    if (step < STEPS.length - 1) return setStep(step + 1);
    setBusy(true);
    try {
      const d = await api("/auth/register", { method: "POST", body: { ...f, birth_year: Number(f.birth_year), parent_email: minor ? f.parent_email : null } });
      if (d.status === "pending_parent") setPending(d.message);
      else { setMember(d.member); toast.success("Willkommen im Team!"); nav("/training/start"); }
    } catch (er) { toast.error(er.message); }
    setBusy(false);
    return undefined;
  };
  const Body = [StepAccount, StepProfile, StepGoals, StepTime, StepEquipment][step];
  return (
    <AuthLayout overline={`Schritt ${step + 1} von ${STEPS.length}`} title={STEPS[step]}>
      <div className="flex gap-1 mb-8" data-testid="reg-progress">{STEPS.map((s, i) => <div key={s} className={`h-1 flex-1 ${i <= step ? "bg-[#c1ff72]" : "bg-white/10"}`} />)}</div>
      <div data-testid={`reg-step-${step}`}><Body f={f} set={set} meta={meta} minor={minor} /></div>
      <div className="flex gap-3 mt-10">
        {step > 0 && <button type="button" onClick={() => setStep(step - 1)} className="h-12 px-5 border border-white/15 text-white" data-testid="reg-back"><ArrowLeft className="w-4 h-4" /></button>}
        <button type="button" disabled={busy} onClick={next} className={btnCls} data-testid="reg-next">{step === STEPS.length - 1 ? (busy ? "…" : "Konto erstellen") : "Weiter"}</button>
      </div>
      <p className="mt-6 text-sm text-[#71717A]">Schon dabei? <Link to="/training/login" className="text-[#c1ff72]" data-testid="reg-to-login">Anmelden</Link></p>
    </AuthLayout>
  );
}
